/**
 * safeStringify: JSON.stringify with built‑in circular‑reference protection.
 *
 * 1. Fast path: run the native JSON.stringify. Most objects are acyclic, so this
 *    finishes with the same performance as the built‑in function.
 * 2. On "Converting circular structure to JSON" errors, we retry with a replacer
 *    that marks repeated objects as "[Circular]".
 *
 * Complexity: O(n) time / space where n is the number of unique objects visited.
 * @param value - The value to stringify.
 * @param space - The number of spaces to use for indentation.
 * @returns The JSON string.
 */
export const safeStringify = (value: unknown, space: number | string = 0): string => {
  const seen = new WeakSet<object>();

  const toPlainError = (err: Error) => {
    const out: Record<string, any> = {
      name: err.name,
      message: err.message,
      stack: err.stack,
    };
    for (const key of Object.keys(err)) {
      try {
        out[key] = (err as any)[key];
      } catch {
        out[key] = '[Unserializable property]';
      }
    }
    const anyErr = err as any;
    if ('cause' in anyErr && anyErr.cause !== undefined) {
      out.cause = anyErr.cause instanceof Error ? toPlainError(anyErr.cause) : anyErr.cause;
    }
    return out;
  };

  const replacer = (_key: string, v: any) => {
    if (v instanceof Error) return toPlainError(v);
    if (typeof v === 'bigint') return v.toString();
    if (v && typeof v === 'object') {
      if (seen.has(v)) return '[Circular]';
      seen.add(v);
    }
    return v;
  };

  const result = JSON.stringify(value as any, replacer as any, space as any);
  return typeof result === 'string' ? result : 'undefined';
};
